<script setup lang="ts">
/**
 * Models & Pricing tab (T07) — live data from the router.
 *
 * Layout (matches reference/img_6.png, img_9.png):
 *   1. "Available Models" intro + grid of capability cards
 *      (sourced from /v1/models JOIN /api/models/capabilities by `id`).
 *   2. "Pricing" table (sourced from /api/pricing).
 *   3. "Last refreshed at …" footer pulled from /api/pricing.refreshed_at,
 *      which is the canonical refresh stamp for the page per T07 spec.
 *
 * Data flow:
 *   - `onMounted` kicks off three parallel fetches.
 *   - Each fetch sets its own loading flag; tab renders progressively so a
 *     slow chain RPC for capabilities doesn't block the pricing table.
 *   - Errors are surfaced inline per-section so partial data is still useful.
 *
 * Capability cards are built by merging `/v1/models` (gives `display_name`)
 * with `/api/models/capabilities` (gives HF id, parsers, VRAM, etc.). If
 * either side is missing for a model, the card still renders with what we
 * have, so a stale cache on either endpoint doesn't blank the UI.
 */
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import {
  fetchCapabilities,
  fetchModels,
  fetchPricing,
  type CapabilityEntry,
  type ModelEntry,
  type PricingResponse,
  type PricingRow,
} from '../../api/docs';
import { formatDateTime, formatUsd } from '../../lib/format';

const models = ref<ModelEntry[]>([]);
const capabilities = ref<CapabilityEntry[]>([]);
const pricing = ref<PricingResponse | null>(null);

const loadingModels = ref(true);
const loadingCaps = ref(true);
const loadingPricing = ref(true);

const errModels = ref<string | null>(null);
const errCaps = ref<string | null>(null);
const errPricing = ref<string | null>(null);

const ac = new AbortController();

interface MergedCard {
  id: string;
  display_name: string;
  context_window: number | null;
  tools_supported: boolean;
  reasoning_supported: boolean;
  per_1m_usd: number | null;
}

const cards = computed<MergedCard[]>(() => {
  // Index capabilities + pricing by id for O(1) lookup.
  const capMap = new Map<string, CapabilityEntry>();
  for (const c of capabilities.value) capMap.set(c.id, c);

  const modelMap = new Map<string, ModelEntry>();
  for (const m of models.value) modelMap.set(m.id, m);

  const priceMap = new Map<string, PricingRow>();
  for (const p of pricing.value?.models ?? []) priceMap.set(p.model, p);

  const ids = new Set<string>();
  for (const m of models.value) ids.add(m.id);
  for (const c of capabilities.value) ids.add(c.id);
  for (const p of pricing.value?.models ?? []) ids.add(p.model);

  const merged: MergedCard[] = [];
  for (const id of ids) {
    const m = modelMap.get(id);
    const c = capMap.get(id);
    const p = priceMap.get(id);
    merged.push({
      id,
      display_name: m?.display_name ?? deriveDisplayName(id),
      context_window: c?.context_window ?? m?.context_window ?? null,
      tools_supported: c?.tools_supported ?? false,
      reasoning_supported: c?.reasoning_supported ?? false,
      per_1m_usd: p?.per_1m_usd ?? null,
    });
  }

  // Stable sort by display name so the grid is deterministic across reloads.
  merged.sort((a, b) => a.display_name.localeCompare(b.display_name));
  return merged;
});

const lastRefreshed = computed<string | null>(() => pricing.value?.refreshed_at ?? null);

onMounted(() => {
  void Promise.allSettled([
    fetchModels(ac.signal)
      .then((r) => {
        // fetchModels already unwraps the OpenAI `{ object, data }` envelope and
        // returns the `ModelEntry[]` array directly.
        models.value = r;
      })
      .catch((err) => {
        if (ac.signal.aborted) return;
        errModels.value = (err as Error).message;
      })
      .finally(() => {
        if (!ac.signal.aborted) loadingModels.value = false;
      }),
    fetchCapabilities(ac.signal)
      .then((r) => {
        capabilities.value = r.models;
      })
      .catch((err) => {
        if (ac.signal.aborted) return;
        errCaps.value = (err as Error).message;
      })
      .finally(() => {
        if (!ac.signal.aborted) loadingCaps.value = false;
      }),
    fetchPricing(ac.signal)
      .then((r) => {
        pricing.value = r;
      })
      .catch((err) => {
        if (ac.signal.aborted) return;
        errPricing.value = (err as Error).message;
      })
      .finally(() => {
        if (!ac.signal.aborted) loadingPricing.value = false;
      }),
  ]);
});

onBeforeUnmount(() => ac.abort());

function deriveDisplayName(id: string): string {
  // Fallback display when /v1/models lacks `display_name`.
  const slash = id.lastIndexOf('/');
  return slash >= 0 ? id.slice(slash + 1) : id;
}

function formatContext(n: number | null): string {
  if (n == null) return '—';
  if (n >= 1000) return `${Math.round(n / 1000)}K context`;
  return `${n} context`;
}

function formatPer1m(usd: number | null): string {
  if (usd == null) return '—';
  if (usd > 0 && usd < 0.01) {
    return `$${usd.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')}`;
  }
  return formatUsd(usd);
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Available Models -->
    <section class="card p-6">
      <div class="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 class="text-lg font-semibold">Available models</h2>
          <p class="text-sm text-muted mt-2 max-w-xl">
            Live from the Gonka network. Pick a model, point your OpenAI client at it, and pay the
            per-1M-token rate shown — prompt and completion are billed the same.
          </p>
        </div>
      </div>

      <div v-if="errCaps && errPricing && cards.length === 0" class="mt-4 text-sm text-danger">
        Couldn’t load the model list. Please refresh in a moment.
      </div>
      <div v-if="loadingCaps && loadingModels && cards.length === 0" class="mt-4 text-sm text-muted">
        Loading models…
      </div>

      <div
        v-if="cards.length > 0"
        class="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
      >
        <article
          v-for="card in cards"
          :key="card.id"
          class="card p-5 flex flex-col gap-4"
        >
          <header class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-ink">{{ card.display_name }}</h3>
              <p class="text-xs font-mono text-muted mt-0.5 break-all">{{ card.id }}</p>
            </div>
            <div class="text-right shrink-0">
              <div class="font-display text-lg font-semibold text-ink leading-none">
                {{ formatPer1m(card.per_1m_usd) }}
              </div>
              <div class="text-[11px] text-muted mt-1">/ 1M tokens</div>
            </div>
          </header>

          <div class="flex flex-wrap gap-1.5">
            <span class="pill">{{ formatContext(card.context_window) }}</span>
            <span v-if="card.tools_supported" class="pill">Tools</span>
            <span v-if="card.reasoning_supported" class="pill">Reasoning</span>
          </div>
        </article>
      </div>

      <p class="text-xs text-muted mt-5">
        Prices are live from the Gonka chain<span v-if="lastRefreshed"> · updated
        {{ formatDateTime(lastRefreshed) }}</span>. New accounts start with
        <span class="text-ok font-semibold">1,000,000 free tokens</span>.
      </p>
    </section>
  </div>
</template>
